import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { SqlDWSink } from 'app/model/pipeline/copysink/sqldw.copysink';
import { SqlDWCopySource } from 'app/model/pipeline/copysource/sqldw.copysource';
import { format } from 'app/shared/utils/string.util';
import {
    azureResourceSelectMethodOptions,
    azureCloudTypeMetadata,
    authenticationReferenceMethodOptions,
    sqlEncryptControl,
    sqlTrustServerCertificateControl,
    sqlAdditionalPropertiesKeyOptions,
    sqlAdditionalPropertiesInFabricKeyOptions,
    connectionStringParseControl
} from '../consts/metadata-consts';
import { azureSqlDomain, sqlPEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import {
    defaultSqlAuthenticationOptionAKV,
    defaultSqlAuthenticationOptionNoAKV,
    mergeUamiCredentialOptionSql
} from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import {
    ConnectionAuthKind,
    CredentialTypeOptions,
    SQLAuthenticationType,
    azureSqlDWAuthRefTypes
} from 'shell-models-lib';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { compact } from 'lodash';

const tenantInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIdInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        description: stringResources.Description,
        descriptionLink:
            'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-data-warehouse?tabs=data-factory#service-principal-authentication'
    }
};

const akvSwitcherInput: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [{ type: 'required', errorMessage: '' }]
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureSynapseAnalytics),
        stringResources.ModelName_ServicePrincipalKey
    )
};

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureSqlDwAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureSqlDWAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureSqlDWAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            tenantInput,
                            servicePrincipalIdInput,
                            akvSwitcherInput,
                            azureCloudTypeMetadata
                        ]
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureSynapseAnalytics,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-data-warehouse?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureSqlDWAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        supportFic: GlobalVariables.supportFic,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

const accountSelectionControl: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureSql_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{serverName != null  || parameterized ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-sql-db-select',
                    bind: {
                        fullyQualifiedDomainName: 'serverName',
                        databaseName: 'databaseName',
                        sqlPoolName: 'sqlPoolName',
                        serverResourceId: '_serverResourceID'
                    },
                    isSqlDW: true,
                    showPEStatus: true,
                    validators: {
                        serverName: [{ type: 'required', errorMessage: '' }],
                        databaseName: [{ type: 'required', errorMessage: '' }]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'serverName',
                        label: stringResources.FullyServerNameLabel,
                        required: true,
                        placeholder: `e.g., myserver.${azureSqlDomain}`,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'databaseName',
                        label: stringResources.ModelName_DatabaseName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
};

const sqlDWAuthControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: mergeUamiCredentialOptionSql(defaultSqlAuthenticationOptionNoAKV),
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    defaultValue: SQLAuthenticationType.SQL,
    cases: [
        {
            caseValue: SQLAuthenticationType.SQL,
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'userName',
                        label: stringResources.ModelName_UserName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    hideAKVSwitcherInConnStrProps: true,
                    bind: 'password',
                    label: stringResources.ModelName_Password,
                    child: {
                        type: 'parameterizable',
                        attribute: 'password',
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_Password,
                            required: true,
                            isLabelVertical: true,
                            validators: [{ type: 'required', errorMessage: '' }]
                        },
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(
                                stringResources.ModelName_LinkedServiceTemplate,
                                stringResources.AzureSynapseAnalytics
                            ),
                            stringResources.ModelName_Password
                        )
                    }
                }
            ]
        },
        {
            caseValue: SQLAuthenticationType.ServicePrincipal,
            children: [azureSqlDwAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
        },
        {
            caseValue: SQLAuthenticationType.ManagedServiceIdentity,
            children: !GlobalVariables.isSamiSupportEnabled
                ? [
                    {
                        type: 'management-identity-info',
                        resourceType: stringResources.AzureSynapseAnalytics,
                        link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-data-warehouse?tabs=data-factory#managed-identity'
                    }
                ]
                : [azureSqlDwAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
        },
        {
            caseValue: SQLAuthenticationType.UserAssignedManagedIdentity,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};

const sqlDWV1LinkedServiceControl: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'azure-key-vault',
                    enableAKVParameterization: true,
                    bind: 'referedAKV',
                    isLabelVertical: true,
                    description: format(
                        stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureSynapseAnalytics),
                        // eslint-disable-next-line max-len
                        `Server=tcp:<servername>.${azureSqlDomain},1433;Database=<databasename>;User ID=<username>@<servername>;Password=<password>;Trusted_Connection=False;Encrypt=True;Connection Timeout=30`
                    )
                },
                {
                    type: 'select-switch',
                    bind: 'authenticationType',
                    label: stringResources.ModelName_AuthenticationType,
                    options: mergeUamiCredentialOptionSql(defaultSqlAuthenticationOptionAKV),
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true,
                    defaultValue: SQLAuthenticationType.SqlOrManagedServiceIdentity,
                    cases: [
                        {
                            caseValue: SQLAuthenticationType.ServicePrincipal,
                            children: [azureSqlDwAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
                        },
                        {
                            caseValue: SQLAuthenticationType.SqlOrManagedServiceIdentity,
                            children: [azureSqlDwAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                        },
                        {
                            caseValue: SQLAuthenticationType.UserAssignedManagedIdentity,
                            children: [
                                {
                                    type: 'credential-select',
                                    bind: 'credential',
                                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }]
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            caseValue: false,
            children: [
                accountSelectionControl,
                sqlDWAuthControl,
                {
                    type: 'key-val-list',
                    bind: 'additionalFields',
                    label: stringResources.AdditionalConnectionStringFields
                }
            ]
        }
    ]
};

const dataflowAzureSqlDW: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.SynapseAnalytics,
    settings: adfModelTypeMap.azureSqlDW.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

export const azureSqlDWMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureSqlDW.linkedServiceType,
        displayName: stringResources.AzureSynapseAnalytics,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-SQLPool'
        },
        supportScriptActivity: true,
        supportStoredProcedureActivity: true,
        disableSinkStoredProcedure: true,
        distinguishTableView: true,
        queryAlias: 'sqlReaderQuery',
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSinkStagingSchema: true,
        dataFlowSinkUseDefaultStagingSchemaLabel: stringResources.UseSinkSchema,
        dataFlowSinkUseDefaultStagingSchemaDescription: stringResources.CustomizedStagingSchemaDescription,
        dataflowSource: [dataflowAzureSqlDW],
        dataflowSink: [dataflowAzureSqlDW],
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.AzureSqlDw
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.Basic,
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        copyJob: { omitConnectionDatabaseFromDataset: true },
        supportMultipleIncrementalColumns: true,
        isFirstClass: true,
        supportPE: true,
        peGroupIDs: ['sqlServer', 'sql', 'sqlOnDemand'],
        resourceTypes: ['Microsoft.Sql/servers', 'Microsoft.Synapse/workspaces'],
        peWellknowErrorMessages: sqlPEWellknownErrorMessages,
        supportDatasetV2: true,
        tableColumnNameQuotes: ['[', ']'],
        supportPreCopyScript: true,
        category: ['Azure'],
        supportGetMetadataActivity: true,
        linkedServiceNamePrefix: 'AzureSynapseAnalytics',
        copySinkTypes: [Metamodel.modelFor(SqlDWSink).name],
        copySourceTypes: [Metamodel.modelFor(SqlDWCopySource).name],
        supportFirstClassParameterExperience: true,
        supportControlTable: true,
        additionalPropertiesInFabric: GlobalVariables.connectionPropertiesForSql ? sqlAdditionalPropertiesInFabricKeyOptions : undefined,
        controls: [
            {
                type: 'radio-switch',
                bind: 'sqlVersion',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [sqlDWV1LinkedServiceControl]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: [
                            connectionStringParseControl(true),
                            accountSelectionControl,
                            sqlDWAuthControl,
                            sqlEncryptControl,
                            sqlTrustServerCertificateControl,
                            {
                                type: 'parameter-config',
                                bind: 'parameterizableAdditionalFields',
                                label: stringResources.AdditionalConnectionStringFields,
                                isLayoutVertical: true,
                                isKeyDropdown: true,
                                keyOptions: sqlAdditionalPropertiesKeyOptions,
                                keyOptionHasType: true,
                                dynamicInputTypes: true,
                                dynamicInputTypesParameterizable: true,
                                keyLabel: stringResources.PropertyName,
                                defaultWidth: 250
                            }
                        ]
                    }
                ]
            }
        ]
    };
    return metadata;
};
